import React from 'react';

import { Heading } from '../../../../../../components/Heading/Heading';
import {
  DEFAULT_MAP_STYLE,
  MAP_STYLE_URLS,
  MapStyle,
} from '../../../../../../components/Maps/constants/constants';
import { ALL_FISHING_WATERS_TOGGLEABLE_GROUPS } from '../../../../../../components/Maps/constants/mapLayers/fishingWaters';
import { ALL_POI_TOGGLEABLE_GROUPS } from '../../../../../../components/Maps/constants/mapLayers/pointsOfInterest';
import { ALL_WAYPOINTS_TOGGLEABLE_GROUPS } from '../../../../../../components/Maps/constants/mapLayers/waypoints';
import { LayerName, ToggleableGroup } from '../../../../../../components/Maps/types/types';
import { useMap } from '../../../../../../contexts/MapContext';
import { useMapLayers } from '../../../../../../contexts/MapLayersContext';
import spotPredictionImage from '../../../../assets/spot-prediction.png';
import {
  catchPosition,
  spotPrediction,
  waypoint,
  ArtificialReefs,
} from '../../../../constants/proModalContent';
import { useExplore } from '../../../../context/ExploreContext';
import { MapDrawerLayout } from '../../MapDrawerLayout';

import { MapLayerSettings } from './components/MapLayerSettings/MapLayerSettings';
import { MapStyleButton } from './components/MapStyleButton/MapStyleButton';

export const MapLayersView = () => {
  const { map, setIsMapPreloaded } = useMap();
  const { exploreQueryParams, saveParamsInUrl, setFilterViewBackButtonPath } = useExplore();
  const { layers, toggleLayer, setNewLayerConfig } = useMapLayers();

  const style = exploreQueryParams?.mapStyle ?? DEFAULT_MAP_STYLE;
  const setStyle = (newStyle: MapStyle): void => {
    if (!map) {
      return;
    }

    if (newStyle === 'hybrid') {
      map.setStyle(MAP_STYLE_URLS.hybrid);
    } else {
      map.setStyle(MAP_STYLE_URLS.terrain);
    }

    setIsMapPreloaded(false);

    map.once('styledata', () => {
      setIsMapPreloaded(true);
    });

    saveParamsInUrl({ mapStyle: newStyle });
  };

  const getActionButtonText = ({
    isActive,
    current,
    max,
  }: {
    isActive: boolean;
    current: number;
    max: number;
  }): string => (isActive && current !== max ? `${current} out of ${max} visible` : 'Settings');

  const isPOILayerActive = layers.pointsOfInterest.isWholeLayerVisible;
  const isWaypointsLayerActive = layers.waypoints.isWholeLayerVisible;
  const isFishingWatersLayerActive = layers.fishingWaters.isWholeLayerVisible;
  const isArtificialReefsLayerActive = layers.artificialReefs.isWholeLayerVisible;

  const presetToggleableGroups = (
    layerName: LayerName,
    isActive: boolean,
    all: ToggleableGroup[],
  ): ToggleableGroup[] => {
    if (!isActive && layers[layerName].toggleableGroups.length === 0) {
      return all;
    }

    return layers[layerName].toggleableGroups;
  };

  return (
    <MapDrawerLayout
      headerContent={
        <Heading as="h1" variant="sm" className="mt-0 mb-6">
          Map layers
        </Heading>
      }
    >
      <Heading as="h2" variant="2xs" className="mb-3">
        Base map
      </Heading>
      <div className="flex gap-3">
        <div className="flex grow justify-center">
          <MapStyleButton mapStyle="hybrid" selectedMapStyle={style} onMapStyleClick={setStyle} />
        </div>
        <div className="flex grow justify-center">
          <MapStyleButton mapStyle="terrain" selectedMapStyle={style} onMapStyleClick={setStyle} />
        </div>
      </div>
      <Heading as="h2" variant="2xs" className="mt-6 mb-3">
        Customize your fishing map
      </Heading>
      <div className="flex flex-col gap-2">
        <div className="flex grow justify-center">
          <MapLayerSettings
            title="Catch positions"
            description="Show public catch locations."
            actionButton="Settings"
            onActionButtonClick={() => {
              setFilterViewBackButtonPath('map-layers');
              saveParamsInUrl({
                drawerView: 'filter-catches',
                drawerViewId: undefined,
              });
            }}
            icon="catch_marker"
            isActive={layers.catchPositions.isWholeLayerVisible}
            onToggle={() => {
              toggleLayer('catchPositions');
            }}
            mapStyle={style}
            analyticsEventName="catch_location"
            proModalContent={catchPosition}
          />
        </div>
        <div className="flex grow justify-center">
          <MapLayerSettings
            title="Spot prediction"
            description="Suggested fishing locations according to artificial intelligence. The smartest way to find new spots."
            image={spotPredictionImage}
            isActive={layers.spotPrediction.isWholeLayerVisible}
            onToggle={() => {
              toggleLayer('spotPrediction');
            }}
            mapStyle={style}
            analyticsEventName="spot_prediction"
            proModalContent={spotPrediction}
          />
        </div>
        <div className="flex grow justify-center">
          <MapLayerSettings
            title="Private waypoints"
            description="Show waypoints that you’ve created in the Fishbrain app."
            actionButton={getActionButtonText({
              isActive: isWaypointsLayerActive,
              current: layers.waypoints.toggleableGroups.length,
              max: ALL_WAYPOINTS_TOGGLEABLE_GROUPS.length,
            })}
            onActionButtonClick={() => {
              saveParamsInUrl({
                drawerView: 'waypoints-settings',
                drawerViewId: undefined,
              });
            }}
            icon="waypoint_default"
            isActive={isWaypointsLayerActive}
            onToggle={() => {
              setNewLayerConfig('waypoints', {
                isWholeLayerVisible: !isWaypointsLayerActive,
                toggleableGroups: presetToggleableGroups(
                  'waypoints',
                  isWaypointsLayerActive,
                  ALL_WAYPOINTS_TOGGLEABLE_GROUPS,
                ),
              });
            }}
            mapStyle={style}
            analyticsEventName="waypoints"
            proModalContent={waypoint}
          />
        </div>
        <div className="flex grow justify-center">
          <MapLayerSettings
            title="Artificial reefs"
            description="Exact locations for 20,000+ artificial reefs"
            isActive={isArtificialReefsLayerActive}
            onToggle={() => {
              toggleLayer('artificialReefs');
            }}
            mapStyle={style}
            icon="artificial_reef"
            analyticsEventName="artificial_reefs"
            proModalContent={ArtificialReefs}
          />
        </div>
        <div className="flex grow justify-center">
          <MapLayerSettings
            title="Points of interest"
            description="Bait shops, boat ramps, marinas and more useful locations."
            actionButton={getActionButtonText({
              isActive: isPOILayerActive,
              current: layers.pointsOfInterest.toggleableGroups.length,
              max: ALL_POI_TOGGLEABLE_GROUPS.length,
            })}
            onActionButtonClick={async () => {
              saveParamsInUrl({ drawerView: 'poi-settings', drawerViewId: undefined });
            }}
            icon="poi_boatramp"
            isActive={isPOILayerActive}
            onToggle={() => {
              setNewLayerConfig('pointsOfInterest', {
                isWholeLayerVisible: !isPOILayerActive,
                toggleableGroups: presetToggleableGroups(
                  'pointsOfInterest',
                  isPOILayerActive,
                  ALL_POI_TOGGLEABLE_GROUPS,
                ),
              });
            }}
            mapStyle={style}
            analyticsEventName="points_of_interest"
          />
        </div>

        <div className="flex grow justify-center">
          <MapLayerSettings
            title="Fishing waters"
            description="Filter waters by catch activity."
            actionButton={getActionButtonText({
              isActive: isFishingWatersLayerActive,
              current: layers.fishingWaters.toggleableGroups.length,
              max: ALL_FISHING_WATERS_TOGGLEABLE_GROUPS.length,
            })}
            onActionButtonClick={async () => {
              saveParamsInUrl({
                drawerView: 'fishing-waters-settings',
                drawerViewId: undefined,
              });
            }}
            icon="fishing_water_with_catches"
            isActive={isFishingWatersLayerActive}
            onToggle={() => {
              setNewLayerConfig('fishingWaters', {
                isWholeLayerVisible: !isFishingWatersLayerActive,
                toggleableGroups: presetToggleableGroups(
                  'fishingWaters',
                  isFishingWatersLayerActive,
                  ALL_FISHING_WATERS_TOGGLEABLE_GROUPS,
                ),
              });
            }}
            mapStyle={style}
            analyticsEventName="fishing_waters"
            mapIconScale={1}
          />
        </div>
      </div>
    </MapDrawerLayout>
  );
};
